import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { athleteAPI, trainingSessionAPI } from '../../services/api';
import { Athlete } from '../../types/mongodb';
import { getQuickDateRange } from './dateRangeUtils';
import { loadAnalyticsFilters, saveAnalyticsFilters, getPagePathFromRoute } from '../../utils/analyticsFilterStorage';
import {
  AnalyticsContainer,
  PageHeader,
  PageTitle,
  PageDescription,
  DataSelectionSection,
  SelectionGrid,
  SelectionGroup,
  GraphSection,
  GraphSectionWithPeriodSelector,
  GraphTitle,
  GraphContainer,
  EmptyState,
  PeriodSelectorTopRight,
  PeriodButtonWrapperSmall,
  PeriodButtonSmall,
  PeriodLabelSmall,
} from './styles';
import {
  DateRangeDropdown,
  AthletesDropdown,
  CategoryDropdown,
} from './FilterDropdowns';
import { getAthleteColor } from '../../utils/athleteColors';
import styled from 'styled-components';

const ListSection = styled.section`
  background: var(--bg-primary);
  border-radius: var(--radius-md);
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid var(--card-border);
  box-shadow: var(--shadow-sm);
`;

const ListTitle = styled.h3`
  font-size: 1.3rem;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-align: left;
`;

const ListTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    white-space: nowrap;
  }
`;

const ListTableHeader = styled.th`
  text-align: center;
  padding: 0.75rem;
  color: var(--text-gray);
  font-size: 0.85rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 0.5rem 0.3rem;
  }
`;

const ListTableCell = styled.td`
  text-align: center;
  padding: 0.75rem;
  color: var(--text-dark);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 0.5rem 0.3rem;
  }

  &.date-cell {
    @media (max-width: 768px) {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
    }
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 0.4rem;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    gap: 0.3rem;
  }
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: var(--button-font-size, 0.6rem);
  color: #666;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  white-space: nowrap;

  &:hover {
    color: #333;
  }

  span {
    font-size: var(--button-font-size, 0.6rem);
    color: #333;
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: var(--button-font-size, 0.6rem);
  color: #dc3545;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  white-space: nowrap;

  &:hover {
    color: #c82333;
  }

  span {
    font-size: var(--button-font-size, 0.6rem);
  }
`;

const ListTableRow = styled.tr`
  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;


interface CompetitionJump {
  athleteId: string | { _id: string; firstName: string; lastName: string };
  result?: number;
}

interface TrainingSession {
  _id: string;
  date: string;
  trainingType: string;
  category?: string;
  location?: string;
  hillSize?: string;
  competitionJumps?: CompetitionJump[];
}

interface BestResult {
  athlete: string;
  category: string;
  result: number;
  date: string;
}

interface CompetitionSessionsAnalyticsProps {
  isJumperMode?: boolean;
}

const CompetitionSessionsAnalytics: React.FC<CompetitionSessionsAnalyticsProps> = ({ isJumperMode = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pagePath = getPagePathFromRoute(location.pathname, searchParams);
  
  // Load saved filters on mount
  const savedFilters = loadAnalyticsFilters(pagePath);
  
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [selectedAthletes, setSelectedAthletes] = useState<string[]>(savedFilters?.selectedAthletes || []);
  const [jumperAthleteId, setJumperAthleteId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>(savedFilters?.startDate || '');
  const [endDate, setEndDate] = useState<string>(savedFilters?.endDate || '');
  const [selectedCategory, setSelectedCategory] = useState<string>(savedFilters?.selectedCategory || '');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isInitialMount = useRef(true);

  // Data states for graphs
  const [resultsLineData, setResultsLineData] = useState<any[]>([]);
  const [competitionsPerMonthData, setCompetitionsPerMonthData] = useState<any[]>([]);
  const [bestResultsList, setBestResultsList] = useState<BestResult[]>([]);
  const [competitionsPerAthleteList, setCompetitionsPerAthleteList] = useState<Array<{ athlete: string; count: number }>>([]);
  const [competitionsPerCategoryList, setCompetitionsPerCategoryList] = useState<Array<{ category: string; count: number }>>([]);
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'general' | 'standings' | 'results'>('general');
  
  // Results tab states
  const [selectedResultsPeriod, setSelectedResultsPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [selectedPointsPeriod, setSelectedPointsPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [selectedResultsAthlete, setSelectedResultsAthlete] = useState<string>('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Set default selected athlete when athletes are selected
  useEffect(() => {
    if (selectedAthletes.length > 0 && !selectedResultsAthlete) {
      setSelectedResultsAthlete(selectedAthletes[0]);
    } else if (selectedAthletes.length > 0 && !selectedAthletes.includes(selectedResultsAthlete)) {
      setSelectedResultsAthlete(selectedAthletes[0]);
    }
  }, [selectedAthletes, selectedResultsAthlete]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Standings data states
  const [standingsByCategory, setStandingsByCategory] = useState<Record<string, Array<{
    athlete: string;
    athleteId: string;
    totalPoints: number;
    bestResult: number | null;
    wins: number;
    secondPlaces: number;
    thirdPlaces: number;
  }>>>({});
  
  // Results tab data states
  const [competitionResultsLineDataDaily, setCompetitionResultsLineDataDaily] = useState<any[]>([]);
  const [competitionResultsLineDataWeekly, setCompetitionResultsLineDataWeekly] = useState<any[]>([]);
  const [competitionResultsLineDataMonthly, setCompetitionResultsLineDataMonthly] = useState<any[]>([]);
  const [pointsLineDataDaily, setPointsLineDataDaily] = useState<any[]>([]);
  const [pointsLineDataWeekly, setPointsLineDataWeekly] = useState<any[]>([]);
  const [pointsLineDataMonthly, setPointsLineDataMonthly] = useState<any[]>([]);
  const [athleteResultsList, setAthleteResultsList] = useState<Array<{
    date: string;
    category: string;
    location: string;
    hillSize: string;
    position: number;
    points: number;
    sessionId: string;
  }>>([]);

  const categoryLabels: Record<string, string> = {
    'olympic_games': 'Olympic Games',
    'world_championships': 'World Championships',
    'world_cup': 'World Cup',
    'summer_grand_prix': 'Summer Grand Prix',
    'continental_cup': 'Continental Cup',
    'fis_cup': 'FIS Cup',
    'alpen_cup': 'Alpen Cup',
    'national_championships': 'National Championships',
    'national_cup': 'National Cup',
    'national': 'National Cup', // Backward compatibility
    'junior_world_championships': 'Junior World Championships',
    'youth_olympic_games': 'Youth Olympic Games',
    'eyof': 'EYOF',
    'opa_games': 'OPA Games',
  };

  // Category hierarchy: lower number = better
  // Order: Olympic Games, World Championships, World Cup, Summer Grand Prix, Continental Cup, FIS Cup, Alpen Cup, National Championships, National Cup, Junior World Championships, Youth Olympic Games, EYOF, OPA Games
  const categoryRank: Record<string, number> = {
    'olympic_games': 1,
    'world_championships': 2,
    'world_cup': 3,
    'summer_grand_prix': 4,
    'continental_cup': 5,
    'fis_cup': 6,
    'alpen_cup': 7,
    'national_championships': 8,
    'national_cup': 9,
    'national': 9, // Backward compatibility
    'junior_world_championships': 10,
    'youth_olympic_games': 11,
    'eyof': 12,
    'opa_games': 13,
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // If jumper mode, get the logged-in jumper's athlete ID first
        if (isJumperMode) {
          try {
            const myAthlete = await athleteAPI.getMyAthlete();
            const athleteId = myAthlete._id.toString();
            setJumperAthleteId(athleteId);
            setSelectedAthletes([athleteId]);
          } catch (error) {
            console.error('Error loading jumper athlete:', error);
          }
        }
        
        const [athletesData, sessionsResponse] = await Promise.all([
          athleteAPI.getAll(),
          trainingSessionAPI.getAll(undefined, undefined, true), // Load all sessions for analytics
        ]);

        // Handle response - when all=true, returns array directly, otherwise paginated object
        const sessionsData = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse.sessions || []);

        setAthletes(athletesData);
        
        // Filter only competition sessions
        const competitionSessions = Array.isArray(sessionsData) ? sessionsData.filter((s: TrainingSession) => 
          s.trainingType === 'competition' && 
          s.competitionJumps && 
          s.competitionJumps.length > 0
        ) : [];
        setSessions(competitionSessions);

        // Set default date range (last 6 months) only if no saved filters exist
        if (!savedFilters || (!savedFilters.startDate && !savedFilters.endDate)) {
          const today = new Date();
          const sixMonthsAgo = new Date();
          sixMonthsAgo.setMonth(today.getMonth() - 6);
          setEndDate(today.toISOString().split('T')[0]);
          setStartDate(sixMonthsAgo.toISOString().split('T')[0]);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Save filters whenever they change (skip on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    saveAnalyticsFilters(pagePath, {
      selectedAthletes,
      startDate,
      endDate,
      selectedCategory,
    });
  }, [pagePath, selectedAthletes, startDate, endDate, selectedCategory]);

  // Helper function to format date for input
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    if (dateString.includes('-') && dateString.length === 10) {
      return dateString;
    }
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };

  // Helper function to check if date is in range
  const isDateInRange = (dateString: string, start: string, end: string): boolean => {
    if (!start || !end) return true;
    return dateString >= start && dateString <= end;
  };

  // Helper function to compare results considering category hierarchy
  const isBetterResult = (result1: BestResult, result2: BestResult): boolean => {
    const rank1 = categoryRank[result1.category] || 999;
    const rank2 = categoryRank[result2.category] || 999;
    
    // Lower rank (better category) is better
    if (rank1 < rank2) return true;
    if (rank1 > rank2) return false;
    
    // Same category: lower result number (better position) is better
    return result1.result < result2.result;
  };

  // Points system (same as Banana Cup)
  const POINT_SYSTEM: Record<number, number> = {
    1: 100, 2: 80, 3: 60, 4: 50, 5: 45, 6: 40, 7: 36, 8: 32, 9: 30, 10: 28,
    11: 26, 12: 24, 13: 22, 14: 20, 15: 18, 16: 16, 17: 14, 18: 13, 19: 12, 20: 11,
    21: 10, 22: 9, 23: 8, 24: 7, 25: 6, 26: 5, 27: 4, 28: 3, 29: 2, 30: 1
  };

  const getPointsForPosition = (position: number): number => {
    return POINT_SYSTEM[position] || 0;
  };

  // Process competition data
  useEffect(() => {
    if (!sessions.length || !startDate || !endDate || selectedAthletes.length === 0) {
      setResultsLineData([]);
      setCompetitionsPerMonthData([]);
      setBestResultsList([]);
      setCompetitionsPerAthleteList([]);
      setCompetitionsPerCategoryList([]);
      return;
    }

    // Filter sessions
    const filteredSessions = sessions.filter(session => {
      if (!isDateInRange(session.date, startDate, endDate)) return false;
      if (selectedCategory && session.category !== selectedCategory) return false;
      return true;
    });

    // Get athletes to process
    const athletesToProcess = athletes.filter(a => selectedAthletes.includes(a._id));

    // Collect results per athlete and date
    const athleteResultsMap = new Map<string, Map<string, number[]>>(); // athleteId -> date -> results[]
    
    // Collect competition counts per athlete per month
    const athleteMonthCompetitionMap = new Map<string, Map<string, number>>(); // athleteId -> monthKey -> count
    
    // Collect competition counts per athlete
    const athleteCompetitionCountMap = new Map<string, number>(); // athleteId -> count
    
    // Collect competition counts per category
    const categoryCompetitionCountMap = new Map<string, number>(); // category -> count
    
    // Collect best results per athlete
    const athleteBestResultMap = new Map<string, BestResult>(); // athleteId -> bestResult

    filteredSessions.forEach(session => {
      if (!session.competitionJumps) return;

      // Count competitions per category
      if (session.category) {
        categoryCompetitionCountMap.set(session.category, (categoryCompetitionCountMap.get(session.category) || 0) + 1);
      }

      const sessionDate = new Date(session.date);
      const monthKey = `${sessionDate.getFullYear()}-${String(sessionDate.getMonth() + 1).padStart(2, '0')}`;
      
      // Track which athletes participated in this session
      const athletesInSession = new Set<string>();

      session.competitionJumps.forEach(jump => {
        const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
        const athleteId = String(athleteIdRaw);
        
        if (!selectedAthletes.includes(athleteId)) return;
        
        // Track athlete participation in this session
        athletesInSession.add(athleteId);
        
        if (!jump.result) return;

        // Add to results map
        if (!athleteResultsMap.has(athleteId)) {
          athleteResultsMap.set(athleteId, new Map());
        }
        const dateMap = athleteResultsMap.get(athleteId)!;
        if (!dateMap.has(session.date)) {
          dateMap.set(session.date, []);
        }
        dateMap.get(session.date)!.push(jump.result);

        // Track best result
        const currentBest = athleteBestResultMap.get(athleteId);
        const newResult: BestResult = {
          athlete: `${athletes.find(a => a._id === athleteId)?.firstName} ${athletes.find(a => a._id === athleteId)?.lastName}`,
          category: session.category || 'unknown',
          result: jump.result,
          date: session.date,
        };
        
        if (!currentBest || isBetterResult(newResult, currentBest)) {
          athleteBestResultMap.set(athleteId, newResult);
        }
      });
      
      // Count competitions per athlete per month (each session counts once per athlete)
      athletesInSession.forEach(athleteId => {
        if (!athleteMonthCompetitionMap.has(athleteId)) {
          athleteMonthCompetitionMap.set(athleteId, new Map());
        }
        const monthMap = athleteMonthCompetitionMap.get(athleteId)!;
        monthMap.set(monthKey, (monthMap.get(monthKey) || 0) + 1);
        
        // Count total competitions per athlete
        athleteCompetitionCountMap.set(athleteId, (athleteCompetitionCountMap.get(athleteId) || 0) + 1);
      });
    });

    // Generate date range for results line chart
    const start = new Date(startDate);
    const end = new Date(endDate);
    const allDates: string[] = [];
    const current = new Date(start);
    while (current <= end) {
      allDates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }

    // Process results line data (only if category is selected)
    const resultsLineChartData: any[] = [];
    if (selectedCategory) {
      allDates.forEach(date => {
        const point: any = { date };
        athletesToProcess.forEach(athlete => {
          const athleteName = `${athlete.firstName} ${athlete.lastName}`;
          const athleteIdStr = String(athlete._id);
          const dateMap = athleteResultsMap.get(athleteIdStr);
          
          if (dateMap && dateMap.has(date)) {
            const results = dateMap.get(date)!;
            // For line chart, we'll show the best (lowest) result of the day
            const best = Math.min(...results);
            point[athleteName] = best;
          } else {
            point[athleteName] = null;
          }
        });
        resultsLineChartData.push(point);
      });
    }

    // Process competitions per month per athlete
    const monthStartDate = new Date(start.getFullYear(), start.getMonth(), 1);
    const competitionsPerMonth: any[] = [];
    const checkMonth = new Date(monthStartDate);
    while (checkMonth <= end) {
      const monthKey = `${checkMonth.getFullYear()}-${String(checkMonth.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = checkMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const monthPoint: any = { month: monthLabel };
      
      // Add competition count for each athlete
      athletesToProcess.forEach(athlete => {
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const athleteIdStr = String(athlete._id);
        const monthMap = athleteMonthCompetitionMap.get(athleteIdStr);
        monthPoint[athleteName] = monthMap?.get(monthKey) || 0;
      });
      
      competitionsPerMonth.push(monthPoint);
      checkMonth.setMonth(checkMonth.getMonth() + 1);
    }

    // Process best results list
    const bestResults: BestResult[] = [];
    athleteBestResultMap.forEach((result) => {
      bestResults.push(result);
    });
    bestResults.sort((a, b) => {
      if (isBetterResult(a, b)) return -1;
      if (isBetterResult(b, a)) return 1;
      return 0;
    });

    // Process competitions per athlete list
    const competitionsPerAthlete: Array<{ athlete: string; count: number }> = [];
    athletesToProcess.forEach(athlete => {
      const athleteIdStr = String(athlete._id);
      competitionsPerAthlete.push({
        athlete: `${athlete.firstName} ${athlete.lastName}`,
        count: athleteCompetitionCountMap.get(athleteIdStr) || 0,
      });
    });
    competitionsPerAthlete.sort((a, b) => b.count - a.count);

    // Process competitions per category list
    const competitionsPerCategory: Array<{ category: string; count: number }> = [];
    categoryCompetitionCountMap.forEach((count, category) => {
      competitionsPerCategory.push({
        category: categoryLabels[category] || category,
        count,
      });
    });
    competitionsPerCategory.sort((a, b) => b.count - a.count);

    setResultsLineData(resultsLineChartData);
    setCompetitionsPerMonthData(competitionsPerMonth);
    setBestResultsList(bestResults);
    setCompetitionsPerAthleteList(competitionsPerAthlete);
    setCompetitionsPerCategoryList(competitionsPerCategory);
  }, [sessions, startDate, endDate, selectedAthletes, selectedCategory, athletes]);

  // Process standings data (for STANDINGS tab)
  useEffect(() => {
    if (!sessions.length || !selectedAthletes.length) {
      setStandingsByCategory({});
      return;
    }

    // Filter sessions by date range and category (if selected)
    const filteredSessions = sessions.filter(session => {
      if (startDate && endDate && !isDateInRange(session.date, startDate, endDate)) return false;
      if (selectedCategory && session.category !== selectedCategory) return false;
      return true;
    });

    // Categories that should show Best Result instead of Points
    const categoriesWithBestResult = ['olympic_games', 'world_championships', 'junior_world_championships', 'youth_olympic_games', 'eyof'];
    
    // Helper function to normalize category (for backward compatibility)
    const normalizeCategory = (cat: string) => {
      return cat === 'national' ? 'national_cup' : cat;
    };
    
    // Process standings per category
    const standingsMap: Record<string, Record<string, {
      athlete: string;
      athleteId: string;
      totalPoints: number;
      bestResult: number | null;
      wins: number;
      secondPlaces: number;
      thirdPlaces: number;
    }>> = {};

    filteredSessions.forEach(session => {
      if (!session.competitionJumps || !session.category) return;

      const category = normalizeCategory(session.category);
      if (!standingsMap[category]) {
        standingsMap[category] = {};
      }

      const useBestResult = categoriesWithBestResult.includes(category);

      // Get all jumps for this session with results
      session.competitionJumps.forEach(jump => {
        const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
        const athleteId = String(athleteIdRaw);
        
        // Only process selected athletes with valid results
        if (!selectedAthletes.includes(athleteId) || jump.result === undefined || jump.result === null) return;

        const athlete = athletes.find(a => a._id === athleteId);
        const athleteName = athlete ? `${athlete.firstName} ${athlete.lastName}` : '';
        
        // Use the actual result (final position)
        const finalPosition = jump.result;
        const points = getPointsForPosition(finalPosition);

        if (!standingsMap[category][athleteId]) {
          standingsMap[category][athleteId] = {
            athlete: athleteName,
            athleteId: athleteId,
            totalPoints: 0,
            bestResult: null,
            wins: 0,
            secondPlaces: 0,
            thirdPlaces: 0,
          };
        }

        if (useBestResult) {
          // Track best result (lower is better)
          if (standingsMap[category][athleteId].bestResult === null || finalPosition < standingsMap[category][athleteId].bestResult!) {
            standingsMap[category][athleteId].bestResult = finalPosition;
          }
        } else {
          // Track points
          standingsMap[category][athleteId].totalPoints += points;
        }
        
        if (finalPosition === 1) {
          standingsMap[category][athleteId].wins += 1;
        } else if (finalPosition === 2) {
          standingsMap[category][athleteId].secondPlaces += 1;
        } else if (finalPosition === 3) {
          standingsMap[category][athleteId].thirdPlaces += 1;
        }
      });
    });

    // Convert to arrays sorted appropriately
    const standingsByCategorySorted: Record<string, Array<{
      athlete: string;
      athleteId: string;
      totalPoints: number;
      bestResult: number | null;
      wins: number;
      secondPlaces: number;
      thirdPlaces: number;
    }>> = {};

    Object.keys(standingsMap).forEach(category => {
      const useBestResult = categoriesWithBestResult.includes(category);
      standingsByCategorySorted[category] = Object.values(standingsMap[category])
        .sort((a, b) => {
          if (useBestResult) {
            // Sort by best result (lower is better), then by wins, second places, third places
            if (a.bestResult === null && b.bestResult === null) return 0;
            if (a.bestResult === null) return 1;
            if (b.bestResult === null) return -1;
            if (a.bestResult !== b.bestResult) return a.bestResult - b.bestResult;
            if (a.wins !== b.wins) return b.wins - a.wins;
            if (a.secondPlaces !== b.secondPlaces) return b.secondPlaces - a.secondPlaces;
            return b.thirdPlaces - a.thirdPlaces;
          } else {
            // Sort by total points
            return b.totalPoints - a.totalPoints;
          }
        });
    });

    setStandingsByCategory(standingsByCategorySorted);
  }, [sessions, startDate, endDate, selectedAthletes, selectedCategory, athletes]);

  // Process results and points data (for RESULTS tab)
  useEffect(() => {
    if (!sessions.length || !selectedAthletes.length) {
      setCompetitionResultsLineDataDaily([]);
      setCompetitionResultsLineDataWeekly([]);
      setCompetitionResultsLineDataMonthly([]);
      setPointsLineDataDaily([]);
      setPointsLineDataWeekly([]);
      setPointsLineDataMonthly([]);
      setAthleteResultsList([]);
      return;
    }

    // Categories that should NOT be included in points graph
    const categoriesExcludedFromPoints = ['olympic_games', 'world_championships', 'junior_world_championships', 'youth_olympic_games', 'eyof'];
    
    // Helper function to normalize category (for backward compatibility)
    const normalizeCategory = (cat: string) => {
      return cat === 'national' ? 'national_cup' : cat;
    };
    
    // Filter sessions
    const filteredSessions = sessions.filter(session => {
      if (startDate && endDate && !isDateInRange(session.date, startDate, endDate)) return false;
      if (selectedCategory && normalizeCategory(session.category || '') !== normalizeCategory(selectedCategory)) return false;
      return session.trainingType === 'competition' && session.competitionJumps && session.competitionJumps.length > 0;
    });

    // Get athletes to process
    const athletesToProcess = athletes.filter(a => selectedAthletes.includes(a._id));

    // Collect results and points per athlete per date
    const athleteResultsMap = new Map<string, Map<string, number[]>>(); // athleteId -> date -> results[]
    const athletePointsMap = new Map<string, Map<string, number>>(); // athleteId -> date -> points
    const athleteResultsListMap = new Map<string, Array<{
      date: string;
      category: string;
      location: string;
      hillSize: string;
      position: number;
      points: number;
      sessionId: string;
    }>>(); // athleteId -> results[]

    filteredSessions.forEach(session => {
      if (!session.competitionJumps || !session.category) return;

      // Skip categories that should not be included in points graph
      const normalizedCategory = normalizeCategory(session.category);
      if (categoriesExcludedFromPoints.includes(normalizedCategory)) return;

      // Process each jump - use actual result (final position) for points calculation
      session.competitionJumps.forEach(jump => {
        const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
        const athleteId = String(athleteIdRaw);
        
        // Only process selected athletes with valid results
        if (!selectedAthletes.includes(athleteId) || jump.result === undefined || jump.result === null) return;

        // Use the actual result (final position) to calculate points
        const finalPosition = jump.result;
        const points = getPointsForPosition(finalPosition);

        // Add to results map (store the actual final position)
        if (!athleteResultsMap.has(athleteId)) {
          athleteResultsMap.set(athleteId, new Map());
        }
        const dateMap = athleteResultsMap.get(athleteId)!;
        if (!dateMap.has(session.date)) {
          dateMap.set(session.date, []);
        }
        dateMap.get(session.date)!.push(finalPosition); // Store actual final position

        // Add to points map
        if (!athletePointsMap.has(athleteId)) {
          athletePointsMap.set(athleteId, new Map());
        }
        const pointsDateMap = athletePointsMap.get(athleteId)!;
        pointsDateMap.set(session.date, (pointsDateMap.get(session.date) || 0) + points);

        // Add to results list
        if (!athleteResultsListMap.has(athleteId)) {
          athleteResultsListMap.set(athleteId, []);
        }
        athleteResultsListMap.get(athleteId)!.push({
          date: session.date,
          category: session.category || '',
          location: session.location || '',
          hillSize: session.hillSize || '',
          position: finalPosition,
          points,
          sessionId: session._id,
        });
      });
    });

    // Sort results lists by date (newest first)
    athleteResultsListMap.forEach(results => {
      results.sort((a, b) => b.date.localeCompare(a.date));
    });

    // Generate date range
    const start = startDate ? new Date(startDate) : new Date();
    const end = endDate ? new Date(endDate) : new Date();
    if (!startDate || !endDate) {
      // If no date range, use all dates from sessions
      filteredSessions.forEach(session => {
        const date = new Date(session.date);
        if (date < start) start.setTime(date.getTime());
        if (date > end) end.setTime(date.getTime());
      });
    }

    // Collect all competition dates (dates where competitions actually happened) per athlete
    const athleteCompetitionDatesMap = new Map<string, Set<string>>(); // athleteId -> Set of competition dates
    filteredSessions.forEach(session => {
      if (session.competitionJumps) {
        session.competitionJumps.forEach(jump => {
          const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          const athleteId = String(athleteIdRaw);
          if (selectedAthletes.includes(athleteId) && jump.result !== undefined && jump.result !== null) {
            if (!athleteCompetitionDatesMap.has(athleteId)) {
              athleteCompetitionDatesMap.set(athleteId, new Set());
            }
            athleteCompetitionDatesMap.get(athleteId)!.add(session.date);
          }
        });
      }
    });

    const allDates: string[] = [];
    const current = new Date(start);
    while (current <= end) {
      allDates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }

    // Process daily data - show all days, but only show points on competition dates
    const resultsDaily: any[] = [];
    const pointsDaily: any[] = [];
    const cumulativePointsMap = new Map<string, number>(); // athleteId -> cumulative points

    allDates.forEach(date => {
      const resultsPoint: any = { date };
      const pointsPoint: any = { date };

      athletesToProcess.forEach(athlete => {
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const athleteIdStr = String(athlete._id);
        const dateMap = athleteResultsMap.get(athleteIdStr);
        const pointsDateMap = athletePointsMap.get(athleteIdStr);
        const competitionDates = athleteCompetitionDatesMap.get(athleteIdStr);

        // Results (best position of the day)
        if (dateMap && dateMap.has(date)) {
          const results = dateMap.get(date)!;
          const bestPosition = Math.min(...results);
          resultsPoint[athleteName] = bestPosition;
        } else {
          resultsPoint[athleteName] = null;
        }

        // Points (cumulative) - update cumulative points if earned on this date
        if (pointsDateMap && pointsDateMap.has(date)) {
          const pointsEarned = pointsDateMap.get(date)!;
          cumulativePointsMap.set(athleteIdStr, (cumulativePointsMap.get(athleteIdStr) || 0) + pointsEarned);
        }

        // Only show points on days when athlete competed
        if (competitionDates && competitionDates.has(date)) {
          pointsPoint[athleteName] = cumulativePointsMap.get(athleteIdStr) || null;
        } else {
          pointsPoint[athleteName] = null;
        }
      });

      resultsDaily.push(resultsPoint);
      pointsDaily.push(pointsPoint);
    });

    // Process weekly data - show all weeks, but only show points for weeks with competitions
    const resultsWeekly: any[] = [];
    const pointsWeekly: any[] = [];
    const weekStart = new Date(start);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1); // Monday

    const cumulativePointsWeeklyMap = new Map<string, number>();

    while (weekStart <= end) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const weekKey = weekStart.toISOString().split('T')[0];
      const resultsPoint: any = { week: weekKey };
      const pointsPoint: any = { week: weekKey };

      athletesToProcess.forEach(athlete => {
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const athleteIdStr = String(athlete._id);
        const dateMap = athleteResultsMap.get(athleteIdStr);
        const pointsDateMap = athletePointsMap.get(athleteIdStr);
        const competitionDates = athleteCompetitionDatesMap.get(athleteIdStr);

        const weekResults: number[] = [];
        let weekPoints = 0;
        let hasCompetitionInWeek = false;
        const checkDate = new Date(weekStart);
        while (checkDate <= weekEnd && checkDate <= end) {
          const dateStr = checkDate.toISOString().split('T')[0];
          if (dateMap && dateMap.has(dateStr)) {
            weekResults.push(...dateMap.get(dateStr)!);
          }
          if (pointsDateMap && pointsDateMap.has(dateStr)) {
            weekPoints += pointsDateMap.get(dateStr)!;
          }
          if (competitionDates && competitionDates.has(dateStr)) {
            hasCompetitionInWeek = true;
          }
          checkDate.setDate(checkDate.getDate() + 1);
        }

        if (weekResults.length > 0) {
          resultsPoint[athleteName] = Math.min(...weekResults);
        } else {
          resultsPoint[athleteName] = null;
        }

        cumulativePointsWeeklyMap.set(athleteIdStr, (cumulativePointsWeeklyMap.get(athleteIdStr) || 0) + weekPoints);
        // Only show points for weeks when athlete competed
        if (hasCompetitionInWeek) {
          pointsPoint[athleteName] = cumulativePointsWeeklyMap.get(athleteIdStr) || null;
        } else {
          pointsPoint[athleteName] = null;
        }
      });

      resultsWeekly.push(resultsPoint);
      pointsWeekly.push(pointsPoint);
      weekStart.setDate(weekStart.getDate() + 7);
    }

    // Process monthly data - show all months, but only show points for months with competitions
    const resultsMonthly: any[] = [];
    const pointsMonthly: any[] = [];
    const monthStart = new Date(start.getFullYear(), start.getMonth(), 1);
    const cumulativePointsMonthlyMap = new Map<string, number>();

    while (monthStart <= end) {
      const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
      const monthLabel = monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const resultsPoint: any = { month: monthLabel };
      const pointsPoint: any = { month: monthLabel };

      athletesToProcess.forEach(athlete => {
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const athleteIdStr = String(athlete._id);
        const dateMap = athleteResultsMap.get(athleteIdStr);
        const pointsDateMap = athletePointsMap.get(athleteIdStr);
        const competitionDates = athleteCompetitionDatesMap.get(athleteIdStr);

        const monthResults: number[] = [];
        let monthPoints = 0;
        let hasCompetitionInMonth = false;
        const checkDate = new Date(monthStart);
        while (checkDate <= monthEnd && checkDate <= end) {
          const dateStr = checkDate.toISOString().split('T')[0];
          if (dateMap && dateMap.has(dateStr)) {
            monthResults.push(...dateMap.get(dateStr)!);
          }
          if (pointsDateMap && pointsDateMap.has(dateStr)) {
            monthPoints += pointsDateMap.get(dateStr)!;
          }
          if (competitionDates && competitionDates.has(dateStr)) {
            hasCompetitionInMonth = true;
          }
          checkDate.setDate(checkDate.getDate() + 1);
        }

        if (monthResults.length > 0) {
          resultsPoint[athleteName] = Math.min(...monthResults);
        } else {
          resultsPoint[athleteName] = null;
        }

        cumulativePointsMonthlyMap.set(athleteIdStr, (cumulativePointsMonthlyMap.get(athleteIdStr) || 0) + monthPoints);
        // Only show points for months when athlete competed
        if (hasCompetitionInMonth) {
          pointsPoint[athleteName] = cumulativePointsMonthlyMap.get(athleteIdStr) || null;
        } else {
          pointsPoint[athleteName] = null;
        }
      });

      resultsMonthly.push(resultsPoint);
      pointsMonthly.push(pointsPoint);
      monthStart.setMonth(monthStart.getMonth() + 1);
    }

    // Set results list for selected athlete (or first athlete if none selected)
    const selectedAthleteId = selectedResultsAthlete || (selectedAthletes.length > 0 ? selectedAthletes[0] : '');
    if (selectedAthleteId && athleteResultsListMap.has(selectedAthleteId)) {
      setAthleteResultsList(athleteResultsListMap.get(selectedAthleteId)!);
    } else {
      setAthleteResultsList([]);
    }

    setCompetitionResultsLineDataDaily(resultsDaily);
    setCompetitionResultsLineDataWeekly(resultsWeekly);
    setCompetitionResultsLineDataMonthly(resultsMonthly);
    setPointsLineDataDaily(pointsDaily);
    setPointsLineDataWeekly(pointsWeekly);
    setPointsLineDataMonthly(pointsMonthly);
  }, [sessions, startDate, endDate, selectedAthletes, selectedCategory, athletes, selectedResultsAthlete]);

  const handleAthleteToggle = (athleteId: string) => {
    setSelectedAthletes(prev =>
      prev.includes(athleteId)
        ? prev.filter(id => id !== athleteId)
        : [...prev, athleteId]
    );
  };

  const handleUpdateDate = () => {
    // Data will be re-processed automatically via useEffect
  };

  const handleQuickDateRange = (range: string) => {
    const { startDate: newStartDate, endDate: newEndDate } = getQuickDateRange(range);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleAthletesSave = (selected: string[]) => {
    setSelectedAthletes(selected);
  };

  const handleEditSession = (sessionId: string) => {
    navigate(`/coach/competition-sessions?edit=${sessionId}`);
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (!window.confirm('Are you sure you want to delete this competition session?')) {
      return;
    }
    
    try {
      await trainingSessionAPI.delete(sessionId);
      // Reload sessions
      const loadSessions = async () => {
        const sessionsResponse = await trainingSessionAPI.getAll(undefined, undefined, true); // Load all sessions
        // Handle response - when all=true, returns array directly, otherwise paginated object
        const sessionsData = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse.sessions || []);
        const competitionSessions = Array.isArray(sessionsData) ? sessionsData.filter((s: TrainingSession) => 
          s.trainingType === 'competition' && 
          s.competitionJumps && 
          s.competitionJumps.length > 0
        ) : [];
        setSessions(competitionSessions);
      };
      loadSessions();
    } catch (error) {
      console.error('Error deleting session:', error);
      alert('Failed to delete session. Please try again.');
    }
  };


  if (isLoading) {
    return (
      <AnalyticsContainer>
        <EmptyState>Loading competition data...</EmptyState>
      </AnalyticsContainer>
    );
  }

  return (
    <AnalyticsContainer>
      <PageHeader>
        <PageTitle>Competition Sessions Analytics</PageTitle>
        <PageDescription>
          Track and analyze competition performance, results, and trends over time.
        </PageDescription>
      </PageHeader>

      {/* Data Selection Section */}
      <DataSelectionSection>
        <SelectionGrid>
          <SelectionGroup>
            <DateRangeDropdown
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onQuickDateRange={handleQuickDateRange}
              onUpdate={handleUpdateDate}
              formatDateForInput={formatDateForInput}
            />
          </SelectionGroup>

          {!isJumperMode && (
            <SelectionGroup>
              <AthletesDropdown
                athletes={athletes}
                selectedAthletes={selectedAthletes}
                onAthleteToggle={handleAthleteToggle}
                onSave={handleAthletesSave}
              />
            </SelectionGroup>
          )}

          <SelectionGroup>
            <CategoryDropdown
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
            />
          </SelectionGroup>
        </SelectionGrid>
      </DataSelectionSection>

      {/* Tabs */}
      <div style={{
          display: 'flex',
          gap: isMobile ? '0.2rem' : '1rem',
          marginBottom: '1rem',
          borderBottom: '2px solid var(--card-border)',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}>
        {(['general', 'results', 'standings'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: isMobile ? '0.3rem 0.45rem' : '0.75rem 1.5rem',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab ? '3px solid var(--accent-orange)' : '3px solid transparent',
              color: activeTab === tab ? 'var(--accent-orange)' : 'var(--text-gray)',
              fontFamily: 'Poppins',
              fontSize: isMobile ? '0.6rem' : '1rem',
              fontWeight: activeTab === tab ? 900 : 600,
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {tab === 'general' ? 'GENERAL' : tab === 'standings' ? 'STANDINGS' : 'RESULTS'}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'general' && (
        <>
          {/* Graphs Section */}
          
          {/* Results Line Chart (only shown when category is selected) */}
      {selectedCategory && (
        <GraphSection>
          <GraphTitle>{isJumperMode ? `Results in ${categoryLabels[selectedCategory] || selectedCategory}` : `Results for Each Athlete in ${categoryLabels[selectedCategory] || selectedCategory}`}</GraphTitle>
          {startDate && endDate && selectedAthletes.length > 0 && resultsLineData.length > 0 ? (
            <GraphContainer>
              <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                <LineChart data={resultsLineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                    tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                    tickFormatter={(value) => {
                      try {
                        const date = new Date(value);
                        if (isNaN(date.getTime())) return value;
                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      } catch {
                        return value;
                      }
                    }}
                  />
                  <YAxis 
                    reversed
                    style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                    tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                  />
                  <Tooltip />
                  <Legend 
                    wrapperStyle={{ fontSize: isMobile ? '0.5rem' : '0.75rem', fontFamily: 'Poppins' }}
                    iconSize={isMobile ? 8 : 12}
                  />
                  {athletes
                    .filter(a => selectedAthletes.includes(a._id))
                    .map((athlete, index) => {
                      const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                      return (
                        <Line
                          key={athlete._id}
                          type="monotone"
                          dataKey={athleteName}
                          stroke={getAthleteColor(athlete, index)}
                          strokeWidth={2}
                          name={athleteName}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                          connectNulls={true}
                        />
                      );
                    })}
                </LineChart>
              </ResponsiveContainer>
            </GraphContainer>
          ) : (
            <EmptyState>
              {!startDate || !endDate 
                ? 'Please select a date range'
                : selectedAthletes.length === 0
                ? 'Please select at least one athlete'
                : 'No data available for the selected filters'}
            </EmptyState>
          )}
        </GraphSection>
      )}

      {/* Competitions Per Month Line Chart */}
      <GraphSection>
        <GraphTitle>{isJumperMode ? 'Number of Competitions per Month' : 'Number of Competitions for Each Athlete per Month'}</GraphTitle>
        {startDate && endDate && selectedAthletes.length > 0 && competitionsPerMonthData.length > 0 ? (
          <GraphContainer>
            <ResponsiveContainer width="100%" height="100%" minHeight={260}>
              <LineChart data={competitionsPerMonthData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                />
                <YAxis 
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: isMobile ? '0.5rem' : '0.75rem', fontFamily: 'Poppins' }} iconSize={isMobile ? 8 : 12} />
                {athletes
                  .filter(a => selectedAthletes.includes(a._id))
                  .map((athlete, index) => {
                    const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                    return (
                      <Line
                        key={athlete._id}
                        type="monotone"
                        dataKey={athleteName}
                        stroke={getAthleteColor(athlete, index)}
                        strokeWidth={2}
                        name={athleteName}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        connectNulls={true}
                      />
                    );
                  })}
              </LineChart>
            </ResponsiveContainer>
          </GraphContainer>
        ) : (
          <EmptyState>
            {!startDate || !endDate 
              ? 'Please select a date range'
              : selectedAthletes.length === 0
              ? 'Please select at least one athlete'
              : 'No data available for the selected filters'}
          </EmptyState>
        )}
      </GraphSection>

      {/* Lists Section */}
      
      {/* Best Results - Hidden in jumper mode */}
      {!isJumperMode && (
      <ListSection>
        <ListTitle>Best Results</ListTitle>
        {bestResultsList.length > 0 ? (
          <ListTable>
            <thead>
              <tr>
                <ListTableHeader>Athlete</ListTableHeader>
                <ListTableHeader>Category</ListTableHeader>
                <ListTableHeader>Result</ListTableHeader>
                <ListTableHeader>Date</ListTableHeader>
              </tr>
            </thead>
            <tbody>
              {bestResultsList.map((item, index) => (
                <ListTableRow key={index}>
                  <ListTableCell>{item.athlete}</ListTableCell>
                  <ListTableCell>{categoryLabels[item.category] || item.category}</ListTableCell>
                  <ListTableCell>{item.result}</ListTableCell>
                  <ListTableCell>{new Date(item.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</ListTableCell>
                </ListTableRow>
              ))}
            </tbody>
          </ListTable>
        ) : (
          <EmptyState>No competition data available</EmptyState>
        )}
      </ListSection>
      )}

      {/* Competitions Per Athlete - Hidden in jumper mode */}
      {!isJumperMode && (
      <ListSection>
        <ListTitle>{isJumperMode ? 'Number of All Competitions' : 'Number of All Competitions for Each Athlete'}</ListTitle>
        {competitionsPerAthleteList.length > 0 ? (
          <ListTable>
            <thead>
              <tr>
                <ListTableHeader>Athlete</ListTableHeader>
                <ListTableHeader>Number of Competitions</ListTableHeader>
              </tr>
            </thead>
            <tbody>
              {competitionsPerAthleteList.map((item, index) => (
                <ListTableRow key={index}>
                  <ListTableCell>{item.athlete}</ListTableCell>
                  <ListTableCell>{item.count}</ListTableCell>
                </ListTableRow>
              ))}
            </tbody>
          </ListTable>
        ) : (
          <EmptyState>No competition data available</EmptyState>
        )}
      </ListSection>
      )}

      {/* Competitions Per Category */}
      <ListSection>
        <ListTitle>Number of Competitions for Each Category</ListTitle>
        {competitionsPerCategoryList.length > 0 ? (
          <ListTable>
            <thead>
              <tr>
                <ListTableHeader>Category</ListTableHeader>
                <ListTableHeader>Number of Competitions</ListTableHeader>
              </tr>
            </thead>
            <tbody>
              {competitionsPerCategoryList.map((item, index) => (
                <ListTableRow key={index}>
                  <ListTableCell>{item.category}</ListTableCell>
                  <ListTableCell>{item.count}</ListTableCell>
                </ListTableRow>
              ))}
            </tbody>
          </ListTable>
        ) : (
          <EmptyState>No competition data available</EmptyState>
        )}
      </ListSection>
        </>
      )}

      {activeTab === 'standings' && (
        <>
          {/* Standings Lists by Category */}
          {Object.keys(standingsByCategory).length > 0 ? (
            Object.keys(standingsByCategory)
              .sort((a, b) => (categoryRank[a] || 999) - (categoryRank[b] || 999))
              .map(category => {
                const standings = standingsByCategory[category];
                if (standings.length === 0) return null;
                
                // Categories that should show Best Result instead of Points
                const categoriesWithBestResult = ['olympic_games', 'world_championships', 'junior_world_championships', 'youth_olympic_games', 'eyof'];
                const useBestResult = categoriesWithBestResult.includes(category);
                
                return (
                  <ListSection key={category}>
                    <ListTitle>Points in Overall Standings - {categoryLabels[category] || category.toUpperCase()}</ListTitle>
                    <ListTable>
                      <thead>
                        <tr>
                          <ListTableHeader>Athlete</ListTableHeader>
                          {useBestResult ? (
                            <ListTableHeader>Best Result</ListTableHeader>
                          ) : (
                            <ListTableHeader>Total Points</ListTableHeader>
                          )}
                          <ListTableHeader>🥇 1st</ListTableHeader>
                          <ListTableHeader>🥈 2nd</ListTableHeader>
                          <ListTableHeader>🥉 3rd</ListTableHeader>
                        </tr>
                      </thead>
                      <tbody>
                        {standings.map((athlete) => (
                          <ListTableRow key={athlete.athleteId}>
                            <ListTableCell>{athlete.athlete}</ListTableCell>
                            {useBestResult ? (
                              <ListTableCell style={{ fontWeight: 700 }}>
                                {athlete.bestResult !== null && athlete.bestResult !== undefined ? athlete.bestResult : '-'}
                              </ListTableCell>
                            ) : (
                              <ListTableCell style={{ fontWeight: 700 }}>{athlete.totalPoints}</ListTableCell>
                            )}
                            <ListTableCell>{athlete.wins}</ListTableCell>
                            <ListTableCell>{athlete.secondPlaces}</ListTableCell>
                            <ListTableCell>{athlete.thirdPlaces}</ListTableCell>
                          </ListTableRow>
                        ))}
                      </tbody>
                    </ListTable>
                  </ListSection>
                );
              })
          ) : (
            <EmptyState>
              {!startDate || !endDate
                ? 'Please select a date range'
                : selectedAthletes.length === 0
                ? 'Please select at least one athlete'
                : 'No standings data available for the selected filters'}
            </EmptyState>
          )}
        </>
      )}

      {activeTab === 'results' && (
        <>
          {/* Competition Results Line Chart */}
          <GraphSectionWithPeriodSelector>
            <GraphTitle>{isJumperMode ? 'Competition Results' : 'Competition Results for Each Athlete'}</GraphTitle>
            {(() => {
              const hasDailyData = competitionResultsLineDataDaily.length > 0 && competitionResultsLineDataDaily.some(d => Object.keys(d).some(k => k !== 'date' && d[k] !== null));
              const hasWeeklyData = competitionResultsLineDataWeekly.length > 0 && competitionResultsLineDataWeekly.some(d => Object.keys(d).some(k => k !== 'week' && d[k] !== null));
              const hasMonthlyData = competitionResultsLineDataMonthly.length > 0 && competitionResultsLineDataMonthly.some(d => Object.keys(d).some(k => k !== 'month' && d[k] !== null));
              const hasAnyData = hasDailyData || hasWeeklyData || hasMonthlyData;
              
              return hasAnyData ? (
                <PeriodSelectorTopRight>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedResultsPeriod === 'daily'}
                      onClick={() => setSelectedResultsPeriod('daily')}
                    >
                      D
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Daily</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedResultsPeriod === 'weekly'}
                      onClick={() => setSelectedResultsPeriod('weekly')}
                    >
                      W
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Weekly</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedResultsPeriod === 'monthly'}
                      onClick={() => setSelectedResultsPeriod('monthly')}
                    >
                      M
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Monthly</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                </PeriodSelectorTopRight>
              ) : null;
            })()}
            {selectedAthletes.length > 0 ? (
              (() => {
                const currentData = selectedResultsPeriod === 'daily' ? competitionResultsLineDataDaily : selectedResultsPeriod === 'weekly' ? competitionResultsLineDataWeekly : competitionResultsLineDataMonthly;
                const dataKey = selectedResultsPeriod === 'daily' ? 'date' : selectedResultsPeriod === 'weekly' ? 'week' : 'month';
                const hasData = currentData.length > 0 && currentData.some(d => Object.keys(d).some(k => k !== dataKey && d[k] !== null));
                
                return hasData ? (
                  <GraphContainer>
                    <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                      <LineChart data={currentData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey={dataKey} 
                          style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                          tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                          tickFormatter={(value) => {
                            if (selectedResultsPeriod === 'monthly') return value;
                            if (selectedResultsPeriod === 'weekly') {
                              try {
                                const date = new Date(value);
                                if (isNaN(date.getTime())) return value;
                                const weekEnd = new Date(date);
                                weekEnd.setDate(weekEnd.getDate() + 6);
                                return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                              } catch {
                                return value;
                              }
                            }
                            try {
                              const date = new Date(value);
                              if (isNaN(date.getTime())) return value;
                              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                            } catch {
                              return value;
                            }
                          }}
                        />
                        <YAxis 
                          reversed
                          style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                          tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                        />
                        <Tooltip />
                        <Legend 
                          wrapperStyle={{ fontSize: isMobile ? '0.5rem' : '0.75rem', fontFamily: 'Poppins' }}
                          iconSize={isMobile ? 8 : 12}
                        />
                        {athletes
                          .filter(a => selectedAthletes.includes(a._id))
                          .map((athlete, index) => {
                            const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                            return (
                              <Line
                                key={athlete._id}
                                type="monotone"
                                dataKey={athleteName}
                                stroke={getAthleteColor(athlete, index)}
                                strokeWidth={2}
                                name={athleteName}
                                dot={{ r: 4 }}
                                activeDot={{ r: 6 }}
                                connectNulls={true}
                              />
                            );
                          })}
                      </LineChart>
                    </ResponsiveContainer>
                  </GraphContainer>
                ) : (
                  <EmptyState>No competition results data available</EmptyState>
                );
              })()
            ) : (
              <EmptyState>Please select at least one athlete</EmptyState>
            )}
          </GraphSectionWithPeriodSelector>

          {/* Collected Points Line Chart */}
          <GraphSectionWithPeriodSelector>
            <GraphTitle>{isJumperMode ? 'Collected Points' : 'Collected Points for Each Athlete'}</GraphTitle>
            {(() => {
              const hasDailyData = pointsLineDataDaily.length > 0 && pointsLineDataDaily.some(d => Object.keys(d).some(k => k !== 'date' && d[k] !== null));
              const hasWeeklyData = pointsLineDataWeekly.length > 0 && pointsLineDataWeekly.some(d => Object.keys(d).some(k => k !== 'week' && d[k] !== null));
              const hasMonthlyData = pointsLineDataMonthly.length > 0 && pointsLineDataMonthly.some(d => Object.keys(d).some(k => k !== 'month' && d[k] !== null));
              const hasAnyData = hasDailyData || hasWeeklyData || hasMonthlyData;
              
              return hasAnyData ? (
                <PeriodSelectorTopRight>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPointsPeriod === 'daily'}
                      onClick={() => setSelectedPointsPeriod('daily')}
                    >
                      D
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Daily</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPointsPeriod === 'weekly'}
                      onClick={() => setSelectedPointsPeriod('weekly')}
                    >
                      W
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Weekly</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPointsPeriod === 'monthly'}
                      onClick={() => setSelectedPointsPeriod('monthly')}
                    >
                      M
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Monthly</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                </PeriodSelectorTopRight>
              ) : null;
            })()}
            {selectedAthletes.length > 0 ? (
              (() => {
                const currentData = selectedPointsPeriod === 'daily' ? pointsLineDataDaily : selectedPointsPeriod === 'weekly' ? pointsLineDataWeekly : pointsLineDataMonthly;
                const dataKey = selectedPointsPeriod === 'daily' ? 'date' : selectedPointsPeriod === 'weekly' ? 'week' : 'month';
                const hasData = currentData.length > 0 && currentData.some(d => Object.keys(d).some(k => k !== dataKey && d[k] !== null));
                
                return hasData ? (
                  <GraphContainer>
                    <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                      <LineChart data={currentData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey={dataKey} 
                          style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                          tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                          tickFormatter={(value) => {
                            if (selectedPointsPeriod === 'monthly') return value;
                            if (selectedPointsPeriod === 'weekly') {
                              try {
                                const date = new Date(value);
                                if (isNaN(date.getTime())) return value;
                                const weekEnd = new Date(date);
                                weekEnd.setDate(weekEnd.getDate() + 6);
                                return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                              } catch {
                                return value;
                              }
                            }
                            try {
                              const date = new Date(value);
                              if (isNaN(date.getTime())) return value;
                              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                            } catch {
                              return value;
                            }
                          }}
                        />
                        <YAxis 
                          style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                          tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                        />
                        <Tooltip />
                        <Legend 
                          wrapperStyle={{ fontSize: isMobile ? '0.5rem' : '0.75rem', fontFamily: 'Poppins' }}
                          iconSize={isMobile ? 8 : 12}
                        />
                        {athletes
                          .filter(a => selectedAthletes.includes(a._id))
                          .map((athlete, index) => {
                            const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                            return (
                              <Line
                                key={athlete._id}
                                type="monotone"
                                dataKey={athleteName}
                                stroke={getAthleteColor(athlete, index)}
                                strokeWidth={2}
                                name={athleteName}
                                dot={{ r: 4 }}
                                activeDot={{ r: 6 }}
                                connectNulls={true}
                              />
                            );
                          })}
                      </LineChart>
                    </ResponsiveContainer>
                  </GraphContainer>
                ) : (
                  <EmptyState>No points data available</EmptyState>
                );
              })()
            ) : (
              <EmptyState>Please select at least one athlete</EmptyState>
            )}
          </GraphSectionWithPeriodSelector>

          {/* Results for Each Athlete List */}
          <ListSection>
            <ListTitle>{isJumperMode ? 'Results' : 'Results for Each Athlete'}</ListTitle>
            {selectedAthletes.length > 0 ? (
              <>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid var(--card-border)', flexWrap: 'wrap' }}>
                  {athletes
                    .filter(a => selectedAthletes.includes(a._id))
                    .map(athlete => (
                      <button
                        key={athlete._id}
                        onClick={() => setSelectedResultsAthlete(athlete._id)}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: selectedResultsAthlete === athlete._id ? 'var(--accent-orange)' : 'transparent',
                          border: 'none',
                          borderBottom: selectedResultsAthlete === athlete._id ? '3px solid var(--accent-orange)' : '3px solid transparent',
                          color: selectedResultsAthlete === athlete._id ? '#fff' : 'var(--text-gray)',
                          fontFamily: 'Poppins',
                          fontSize: '0.9rem',
                          fontWeight: selectedResultsAthlete === athlete._id ? 900 : 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        {athlete.firstName} {athlete.lastName}
                      </button>
                    ))}
                </div>
                {selectedResultsAthlete && athleteResultsList.length > 0 ? (
                  <ListTable>
                    <thead>
                      <tr>
                        <ListTableHeader>Date</ListTableHeader>
                        <ListTableHeader>Category</ListTableHeader>
                        <ListTableHeader>Location</ListTableHeader>
                        <ListTableHeader>Hill Size</ListTableHeader>
                        <ListTableHeader>Final Position</ListTableHeader>
                        {!isJumperMode && <ListTableHeader>Actions</ListTableHeader>}
                      </tr>
                    </thead>
                    <tbody>
                      {athleteResultsList.map((item, index) => {
                        // Helper function to format position with ordinal suffix
                        const formatPosition = (pos: number): string => {
                          const j = pos % 10;
                          const k = pos % 100;
                          if (j === 1 && k !== 11) {
                            return pos + 'st';
                          }
                          if (j === 2 && k !== 12) {
                            return pos + 'nd';
                          }
                          if (j === 3 && k !== 13) {
                            return pos + 'rd';
                          }
                          return pos + 'th';
                        };

                        return (
                          <ListTableRow key={index}>
                            <ListTableCell className="date-cell">
                              <span>{new Date(item.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                              {!isJumperMode && isMobile && (
                                <ActionButtonsContainer style={{ marginLeft: 'auto' }}>
                                  <EditButton
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleEditSession(item.sessionId);
                                    }}
                                    title="Edit session"
                                  >
                                    <span>✎</span>
                                  </EditButton>
                                  <DeleteButton
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDeleteSession(item.sessionId);
                                    }}
                                    title="Delete session"
                                  >
                                    <span>🗑️</span>
                                  </DeleteButton>
                                </ActionButtonsContainer>
                              )}
                            </ListTableCell>
                            <ListTableCell>{categoryLabels[item.category] || item.category}</ListTableCell>
                            <ListTableCell>{item.location}</ListTableCell>
                            <ListTableCell>{item.hillSize}</ListTableCell>
                            <ListTableCell>{formatPosition(item.position)}</ListTableCell>
                            {!isJumperMode && !isMobile && (
                              <ListTableCell>
                                <ActionButtonsContainer>
                                  <EditButton
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleEditSession(item.sessionId);
                                    }}
                                    title="Edit session"
                                  >
                                    <span>✎</span>
                                  </EditButton>
                                  <DeleteButton
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDeleteSession(item.sessionId);
                                    }}
                                    title="Delete session"
                                  >
                                    <span>🗑️</span>
                                  </DeleteButton>
                                </ActionButtonsContainer>
                              </ListTableCell>
                            )}
                          </ListTableRow>
                        );
                      })}
                    </tbody>
                  </ListTable>
                ) : (
                  <EmptyState>
                    {!selectedResultsAthlete 
                      ? 'Please select an athlete'
                      : 'No results available for this athlete'}
                  </EmptyState>
                )}
              </>
            ) : (
              <EmptyState>Please select at least one athlete</EmptyState>
            )}
          </ListSection>
        </>
      )}
    </AnalyticsContainer>
  );
};

export default CompetitionSessionsAnalytics;
